/*
 * Copacul — ilustrație SVG procedurală, organică și matură (ton editorial, nu cartoon).
 * Coroana e construită din contururi NEREGULATE (bezier), nu cercuri perfecte, cu straturi
 * tonale pentru adâncime. 7 stadii × 4 anotimpuri × vreme (calm / vânt / furtună).
 * `simple` = fără animații grele (pt. randarea Pădurii, zeci de copaci).
 */

// Paletă matură, dessaturată (sage/olive, scoarță caldă), nu verzuri de creion.
const SEASON = {
  primavara: { c: ["#6f8f5e", "#84a06e", "#9db884"], bloom: "#e7b7c4", ground: "#7c8a5f", sky: ["#eef1e8", "#f6f3ea"] },
  vara: { c: ["#4f7a54", "#5f8a5e", "#7aa06f"], bloom: "#e8c36b", ground: "#6e7d52", sky: ["#e9eee2", "#f6f3ea"] },
  toamna: { c: ["#a9743e", "#c1904a", "#d3a95e"], bloom: "#c96a4c", ground: "#8a7a4e", sky: ["#f1e8d9", "#f6f3ea"] },
  iarna: { c: ["#8fa39c", "#a7b8b0", "#c2d0c8"], bloom: "#e8eef0", ground: "#aab3ab", sky: ["#eef2f0", "#f6f3ea"] },
};
const BARK = "#6d5640";
const BARK_D = "#4f3d2c";

// PRNG determinist (mulberry32-lite) — silueta e stabilă între randări.
function rng(seed) {
  let s = seed >>> 0;
  return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
}
// Contur organic închis (catmull-rom → bezier) în jurul unui centru.
function organic(cx, cy, r, n, jitter, rnd, squash = 0.86) {
  const p = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    const rr = r * (1 - jitter + rnd() * jitter * 2);
    p.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr * squash]);
  }
  let d = `M${p[0][0].toFixed(1)},${p[0][1].toFixed(1)}`;
  for (let i = 0; i < n; i++) {
    const p0 = p[(i - 1 + n) % n], p1 = p[i], p2 = p[(i + 1) % n], p3 = p[(i + 2) % n];
    const c1x = p1[0] + (p2[0] - p0[0]) / 6, c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6, c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += `C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)}`;
  }
  return d + "Z";
}

export default function TreeSvg({ stage = 1, season = "vara", size = 260, showBird = true, weather = "calm", simple = false, bare = false }) {
  const s = SEASON[season] || SEASON.vara;
  const winter = season === "iarna";
  const windy = weather === "wind" || weather === "storm";
  const storm = weather === "storm";
  const uid = `t${stage}${season}${weather}${Math.round(size)}`;
  const anim = !simple;

  const crownR = [0, 0, 20, 34, 50, 66, 76, 86][stage] || 44;
  const trunkH = [0, 0, 24, 42, 64, 86, 102, 118][stage] || 60;
  const trunkW = [0, 0, 5, 7, 10, 13, 15, 18][stage] || 9;
  const cx = 130, groundY = 208;
  const trunkTop = groundY - trunkH;
  const cyCrown = trunkTop - crownR * 0.28;

  const hasCrown = stage >= 3;
  const hasFlowers = stage === 5;
  const hasFruit = stage >= 6;
  const hasBench = stage === 7;
  const hasBird = showBird && stage >= 4 && !storm;

  const sway = storm ? 2.6 : windy ? 1.5 : 0.6;
  const swayDur = storm ? "2.4s" : windy ? "3.4s" : "6.5s";

  // Straturi de coroană (spate întunecat → față deschis) + câțiva „bulgări" de textură
  const seed = (stage * 131 + season.length * 17 + 7);
  const layers = hasCrown && !winter ? [
    { d: organic(cx - crownR * 0.05, cyCrown + crownR * 0.06, crownR * 1.04, 12, 0.16, rng(seed), 0.9), fill: s.c[0] },
    { d: organic(cx - crownR * 0.14, cyCrown - crownR * 0.05, crownR * 0.9, 11, 0.18, rng(seed + 3), 0.9), fill: s.c[1] },
    { d: organic(cx + crownR * 0.16, cyCrown - crownR * 0.1, crownR * 0.78, 11, 0.2, rng(seed + 9), 0.9), fill: s.c[1] },
    { d: organic(cx + crownR * 0.02, cyCrown - crownR * 0.22, crownR * 0.66, 10, 0.22, rng(seed + 15), 0.9), fill: s.c[2] },
  ] : [];
  // Poziții „pe coroană" pt flori/fructe (deterministe, în interiorul siluetei)
  const rndP = rng(seed + 40);
  const onCrown = Array.from({ length: 6 }).map(() => {
    const a = rndP() * Math.PI * 2, rr = crownR * (0.25 + rndP() * 0.5);
    return [cx + Math.cos(a) * rr, cyCrown + Math.sin(a) * rr * 0.82];
  });

  return (
    <svg viewBox="0 0 260 240" width={size} height={size * 240 / 260} role="img" aria-label={`Copac, stadiul ${stage}`}>
      <defs>
        <radialGradient id={`sky-${uid}`} cx="50%" cy="34%" r="82%">
          <stop offset="0%" stopColor={storm ? "#cfd3d4" : s.sky[0]} />
          <stop offset="100%" stopColor={storm ? "#e2e2dd" : s.sky[1]} />
        </radialGradient>
        <linearGradient id={`bark-${uid}`} x1="0" x2="1">
          <stop offset="0%" stopColor={BARK_D} /><stop offset="45%" stopColor={BARK} /><stop offset="100%" stopColor={BARK_D} />
        </linearGradient>
        <radialGradient id={`glow-${uid}`} cx="50%" cy="45%" r="55%">
          <stop offset="0%" stopColor="#fff4d6" stopOpacity="0.5" /><stop offset="100%" stopColor="#fff4d6" stopOpacity="0" />
        </radialGradient>
        <filter id={`soft-${uid}`} x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="0.5" /></filter>
      </defs>

      {!bare && <rect x="0" y="0" width="260" height="240" fill={`url(#sky-${uid})`} />}
      {weather === "calm" && hasCrown && !winter && !bare && (
        <ellipse cx={cx + crownR * 0.2} cy={cyCrown} rx={crownR * 1.7} ry={crownR * 1.5} fill={`url(#glow-${uid})`}>
          {anim && <animate attributeName="opacity" values="0.55;1;0.55" dur="6s" repeatCount="indefinite" />}
        </ellipse>
      )}

      {/* umbra la sol + sol */}
      <ellipse cx={cx} cy={groundY + 7} rx={Math.max(24, crownR * 1.1)} ry="9" fill={BARK_D} opacity="0.10" />
      <ellipse cx={cx} cy={groundY + 4} rx="60" ry="8" fill={s.ground} opacity="0.45" />

      {/* STADIUL 1 — sămânța */}
      {stage === 1 && (
        <g>
          <path d={`M${cx - 9} ${groundY} q9 7 18 0 q-4 -7 -9 -7 q-5 0 -9 7Z`} fill={BARK} />
          <circle cx={cx} cy={groundY - 3} r="2.4" fill={s.bloom}>{anim && <animate attributeName="opacity" values="0.4;1;0.4" dur="2.6s" repeatCount="indefinite" />}</circle>
        </g>
      )}

      {/* STADIUL 2 — încolțirea */}
      {stage === 2 && (
        <g style={{ transformOrigin: `${cx}px ${groundY}px` }}>
          {anim && <animateTransform attributeName="transform" type="rotate" values={`-${sway} ${cx} ${groundY};${sway} ${cx} ${groundY};-${sway} ${cx} ${groundY}`} dur={swayDur} repeatCount="indefinite" />}
          <path d={`M${cx} ${groundY} C${cx - 2} ${groundY - 12} ${cx - 1} ${groundY - 22} ${cx} ${groundY - 28}`} stroke={s.c[1]} strokeWidth="3" fill="none" strokeLinecap="round" />
          <path d={organic(cx - 6, groundY - 22, 7, 8, 0.2, rng(5), 0.7)} fill={s.c[2]} />
          <path d={organic(cx + 6, groundY - 27, 7, 8, 0.2, rng(9), 0.7)} fill={s.c[1]} />
        </g>
      )}

      {/* STADIILE 3-7 */}
      {stage >= 3 && (
        <g>
          {/* trunchi curbat, tapered */}
          <path
            d={`M${cx - trunkW / 2} ${groundY}
                C ${cx - trunkW / 2 - 3} ${trunkTop + trunkH * 0.45}, ${cx - trunkW / 3.5} ${trunkTop + 8}, ${cx - trunkW / 5} ${trunkTop}
                L ${cx + trunkW / 5} ${trunkTop}
                C ${cx + trunkW / 3.5} ${trunkTop + 8}, ${cx + trunkW / 2 + 3} ${trunkTop + trunkH * 0.45}, ${cx + trunkW / 2} ${groundY} Z`}
            fill={`url(#bark-${uid})`}
          />
          {stage >= 4 && (
            <g stroke={BARK} fill="none" strokeLinecap="round">
              <path d={`M${cx - 1} ${trunkTop + 20} C ${cx - 12} ${trunkTop + 14} ${cx - 22} ${trunkTop + 4} ${cx - 30} ${trunkTop - 8}`} strokeWidth={trunkW * 0.36} />
              <path d={`M${cx + 1} ${trunkTop + 28} C ${cx + 12} ${trunkTop + 22} ${cx + 22} ${trunkTop + 10} ${cx + 30} ${trunkTop - 4}`} strokeWidth={trunkW * 0.36} />
            </g>
          )}

          {/* coroana — se leagănă din baza trunchiului */}
          <g style={{ transformOrigin: `${cx}px ${groundY}px` }}>
            {anim && <animateTransform attributeName="transform" type="rotate" values={`-${sway} ${cx} ${groundY};${sway} ${cx} ${groundY};-${sway} ${cx} ${groundY}`} dur={swayDur} repeatCount="indefinite" />}
            {!winter && layers.map((l, i) => (
              <path key={i} d={l.d} fill={l.fill} filter={`url(#soft-${uid})`} />
            ))}
            {/* iarna: ramuri golașe cu urme de zăpadă */}
            {winter && (
              <g stroke={BARK} strokeWidth="1.5" fill="none" strokeLinecap="round">
                {[-1, -0.45, 0.15, 0.7].map((k, i) => (
                  <path key={i} d={`M${cx} ${trunkTop} C ${cx + k * crownR * 0.5} ${trunkTop - crownR * 0.4} ${cx + k * crownR} ${trunkTop - crownR * 0.7} ${cx + k * crownR * 1.15} ${trunkTop - crownR}`} />
                ))}
                {[[-crownR * 0.6, -crownR * 0.7], [crownR * 0.5, -crownR * 0.85], [0, -crownR]].map(([dx, dy], i) => (
                  <circle key={i} cx={cx + dx} cy={trunkTop + dy} r="2.6" fill={s.bloom} stroke="none" />
                ))}
              </g>
            )}
            {/* flori */}
            {hasFlowers && onCrown.slice(0, 5).map(([x, y], i) => (
              <g key={i} transform={`translate(${x} ${y})`}>
                <circle r="3.6" fill={s.bloom}>{anim && <animate attributeName="r" values="2.8;4.2;2.8" dur={`${3.4 + i * 0.5}s`} repeatCount="indefinite" />}</circle>
                <circle r="1.3" fill="#fffaf0" />
              </g>
            ))}
            {/* fructe */}
            {hasFruit && onCrown.slice(0, 5).map(([x, y], i) => (
              <circle key={i} cx={x} cy={y} r="3.6" fill="#cf9236" stroke="#a9722a" strokeWidth="0.5" />
            ))}
          </g>
        </g>
      )}

      {/* vânt: frunze purtate */}
      {anim && windy && hasCrown && !winter && [0, 1, 2].map((i) => (
        <path key={i} d="M0 0 C3 -3 6 -3 7 0 C6 3 3 3 0 0Z" fill={s.c[2]} opacity="0.85">
          <animateMotion dur={`${2.6 + i * 0.6}s`} repeatCount="indefinite" path={`M${cx + crownR * 0.5} ${cyCrown} q40 ${8 + i * 10} 95 ${-16 + i * 14}`} />
          <animateTransform attributeName="transform" type="rotate" values="0;360" dur={`${1 + i * 0.4}s`} repeatCount="indefinite" additive="sum" />
        </path>
      ))}

      {/* furtună: ploaie (hrană, nu pedeapsă) */}
      {anim && storm && Array.from({ length: 14 }).map((_, i) => (
        <line key={i} x1={18 + i * 16} y1="6" x2={14 + i * 16} y2="24" stroke="#93b6d1" strokeWidth="1.4" strokeLinecap="round" opacity="0.6">
          <animate attributeName="y1" values="0;232" dur={`${0.85 + (i % 5) * 0.2}s`} repeatCount="indefinite" begin={`${(i % 7) * 0.12}s`} />
          <animate attributeName="y2" values="18;250" dur={`${0.85 + (i % 5) * 0.2}s`} repeatCount="indefinite" begin={`${(i % 7) * 0.12}s`} />
        </line>
      ))}

      {hasBird && (
        <g transform={`translate(${cx + crownR * 0.55} ${cyCrown - crownR * 0.35})`}>
          <path d="M0 0 q4 -4 8 0 q4 -4 8 0" stroke={BARK_D} strokeWidth="1.5" fill="none" strokeLinecap="round">
            {anim && <animateTransform attributeName="transform" type="translate" values="0 0; 3 -2; 0 0" dur="3.2s" repeatCount="indefinite" />}
          </path>
        </g>
      )}

      {hasBench && (
        <g transform={`translate(${cx - 48} ${groundY - 1})`} stroke={BARK_D} strokeWidth="2.2" strokeLinecap="round">
          <line x1="0" y1="0" x2="26" y2="0" /><line x1="2" y1="0" x2="2" y2="8" /><line x1="24" y1="0" x2="24" y2="8" /><line x1="0" y1="-7" x2="26" y2="-7" opacity="0.7" />
        </g>
      )}
    </svg>
  );
}
